import React, { useCallback, useContext, useMemo } from "react";
import { SettingsSection } from "components/Settings/SettingsSection";
import {
  Button,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import { TextField } from "components/TextField";
import { styled } from "@mui/material/styles";
import Paper from "@mui/material/Paper";
import { useFormik } from "formik";
import * as yup from "yup";

import {
  EmailScheduleSettingsResponse,
  UpdateEmailScheduleSettings
} from "interfaces/email-settings";
import { useEmailScheduleSettingsQuery } from "hooks/queries/useEmailScheduleSettingsQuery";
import { useMutation } from "@tanstack/react-query";
import { API_URL_EMAIL_SCHEDULE_SETTINGS } from "constants/api_urls";
import { AxiosContext } from "contexts/AxiosContext";
import { SnackbarQueueContext } from "contexts/SnackbarQueueContext";
import ProgressIndicatorContainer from "components/ProgressIndicatorContainer";
import { useLoadCounter } from "hooks/useLoadCounter";
import { FormikState } from "formik/dist/types";

const validationSchema = yup.object({
  dailyEmailEnabled: yup.boolean().required("Daily Email Enabled is required"),
  dailyEmailSchedule: yup.string().required("Daily Email Schedule is required"),
  dailyEmailSysUserOnlyNotifyOnAssign: yup
    .boolean()
    .required("Daily Email Only Notify On Assign is required")
});

export const EmailScheduleSettingsSection = (): JSX.Element => {
  const { axios } = useContext(AxiosContext)!;
  const { addSnackbarToQueue } = useContext(SnackbarQueueContext);

  const {
    isLoading: isEmailSettingsLoading,
    error: emailSettingsError,
    data: emailScheduleSettings
  } = useEmailScheduleSettingsQuery();

  const patchEmailScheduleSettings = useMutation<
    EmailScheduleSettingsResponse,
    unknown,
    UpdateEmailScheduleSettings
  >(["edit-email-schedule"], {
    mutationFn: (newSettings: UpdateEmailScheduleSettings) =>
      axios
        .patch(`${API_URL_EMAIL_SCHEDULE_SETTINGS}`, newSettings)
        .then(response => response.data)
  });

  const {
    isLoading: isPatchEmailSettingsLoading,
    error: patchEmailSettingsError
  } = patchEmailScheduleSettings;
  type FormValuesType = typeof initialValues;

  const handleSubmit = useCallback(
    async (
      isValid: boolean,
      values: FormValuesType,
      resetForm: (
        nextState?: Partial<FormikState<FormValuesType>> | undefined
      ) => void
    ): Promise<void> => {
      if (!isValid || isEmailSettingsLoading) {
        return;
      } else {
        try {
          // Perform the mutation and receive the updated data
          const updatedData = await patchEmailScheduleSettings.mutateAsync({
            daily_email_enabled: values.dailyEmailEnabled,
            daily_email_schedule_time: values.dailyEmailSchedule,
            daily_email_sys_user_only_notify_on_assign:
              values.dailyEmailSysUserOnlyNotifyOnAssign
          });

          // Reset the form with the updated data as the new initial values
          resetForm({
            values: {
              dailyEmailEnabled: updatedData.daily_email_enabled,
              dailyEmailSchedule: updatedData.daily_email_schedule_time,
              dailyEmailSysUserOnlyNotifyOnAssign:
                updatedData.daily_email_sys_user_only_notify_on_assign
            }
          });

          addSnackbarToQueue({
            message: "Email Schedule Settings successfully update..",
            severity: "success"
          });
        } catch (err) {
          addSnackbarToQueue({
            message: "Error updating Email Schedule Settings.",
            severity: "error"
          });
        }
      }
    },
    [addSnackbarToQueue, patchEmailScheduleSettings, isEmailSettingsLoading]
  );

  const loading = isEmailSettingsLoading || isPatchEmailSettingsLoading;
  const error =
    (emailSettingsError || patchEmailSettingsError) instanceof Error;
  const tableLoadCount = useLoadCounter(loading);

  const initialValues = useMemo(() => {
    return {
      dailyEmailEnabled: emailScheduleSettings?.daily_email_enabled ?? false,
      dailyEmailSchedule:
        emailScheduleSettings?.daily_email_schedule_time ?? "",
      dailyEmailSysUserOnlyNotifyOnAssign:
        emailScheduleSettings?.daily_email_sys_user_only_notify_on_assign ??
        false
    };
  }, [emailScheduleSettings]);

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: values => {
      handleSubmit(formik.isValid, values, formik.resetForm);
    }
  });

  return (
    <SettingsSection title="Email Schedule Settings">
      <ProgressIndicatorContainer loading={loading} error={error}>
        <form onSubmit={formik.handleSubmit} noValidate>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell data-col={1}>Name</TableCell>
                  <TableCell data-col={2}>Value</TableCell>
                </TableRow>
              </TableHead>
              <TableBody
                data-qa_loading={loading}
                data-qa_load_count={tableLoadCount}
              >
                <TableRow>
                  <TableCell data-col={1}>Daily Email Enabled</TableCell>
                  <TableCell data-col={2}>
                    <Switch
                      name="daily_email_enabled"
                      data-qa="Settings-Toggle-Emails-Enabled"
                      inputProps={{
                        "aria-label": "toggle email schedule"
                      }}
                      data-testid="email-schedule-toggle"
                      checked={formik.values.dailyEmailEnabled ?? false}
                      onChange={event =>
                        formik.setFieldValue(
                          "dailyEmailEnabled",
                          event.target.checked
                        )
                      }
                      disabled={loading}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell data-col={1}>
                    Daily Email Schedule Time (UTC)
                  </TableCell>
                  <TableCell data-col={2}>
                    <FixedWidthTextField
                      id="time"
                      data-qa="Settings-Schedule-Time"
                      fullWidth
                      label="Schedule Time (UTC)"
                      type="time"
                      name="daily_email_schedule_time"
                      value={formik.values.dailyEmailSchedule ?? ""}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        formik.setFieldValue(
                          "dailyEmailSchedule",
                          event.target.value
                        )
                      }
                      InputLabelProps={{
                        shrink: true
                      }}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell data-col={1}>
                    When a system user creates a notification, only include
                    notifications where you are assigned to a case.
                  </TableCell>
                  <TableCell data-col={2}>
                    <Switch
                      name="daily_email_sys_user_only_notify_on_assign"
                      data-qa="Settings-Toggle-Emails-Sys-User-Only-Notify-On-Assign"
                      inputProps={{
                        "aria-label": "toggle sys user only notify on assign "
                      }}
                      data-testid="email-sys-user-only-notify-on-assign-toggle"
                      checked={
                        formik.values.dailyEmailSysUserOnlyNotifyOnAssign ??
                        false
                      }
                      onChange={event =>
                        formik.setFieldValue(
                          "dailyEmailSysUserOnlyNotifyOnAssign",
                          event.target.checked
                        )
                      }
                      disabled={loading}
                    />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <ButtonWrapper>
            {error && (
              <Typography color="error">
                {"An error occurred while trying to save"}
              </Typography>
            )}
            <Button
              type="submit"
              data-qa="Settings-Button-Save-Email-Schedule-Settings"
              color="primary"
              variant="contained"
              disabled={
                loading ||
                !formik.dirty ||
                !formik.isValid ||
                formik.isSubmitting
              }
            >
              Save
            </Button>
          </ButtonWrapper>
        </form>
      </ProgressIndicatorContainer>
    </SettingsSection>
  );
};

const ButtonWrapper = styled("div")`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: ${({ theme }) => theme.spacing(1)};
  gap: ${({ theme }) => theme.spacing(1)};
`;

const FixedWidthTextField = styled(TextField)`
  max-width: 200px;
`;
